import { Show } from '@refinedev/antd'
import { useShow } from '@refinedev/core'
import {
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Space,
  Button,
  Image,
  Descriptions,
  Alert,
  Divider,
  message,
} from 'antd'
import {
  InstagramOutlined,
  FacebookOutlined,
  TwitterOutlined,
  SendOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useInvalidate } from '@refinedev/core'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const { Text, Paragraph } = Typography

type SocialPlatform = 'INSTAGRAM' | 'FACEBOOK' | 'TIKTOK' | 'TWITTER'
type SocialPostStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'CANCELLED'

interface PlatformPublishResult {
  success: boolean
  platformPostId?: string
  platformUrl?: string
  error?: string
}

interface SocialPost {
  id: string
  content: string
  contentHtml: string | null
  mediaUrls: string[]
  mediaTypes: string[]
  thumbnailUrl: string | null
  platforms: SocialPlatform[]
  scheduledAt: string | null
  publishedAt: string | null
  timezone: string
  status: SocialPostStatus
  errorMessage: string | null
  publishResults: Record<string, PlatformPublishResult> | null
  createdById: string
  createdAt: string
  updatedAt: string
}

const statusConfig: Record<SocialPostStatus, { color: string; text: string; icon: React.ReactNode }> = {
  DRAFT: { color: 'default', text: 'Nacrt', icon: <ClockCircleOutlined /> },
  SCHEDULED: { color: 'blue', text: 'Zakazano', icon: <ClockCircleOutlined /> },
  PUBLISHING: { color: 'processing', text: 'Objavljuje se', icon: <SendOutlined /> },
  PUBLISHED: { color: 'success', text: 'Objavljeno', icon: <CheckCircleOutlined /> },
  FAILED: { color: 'error', text: 'Neuspelo', icon: <ExclamationCircleOutlined /> },
  CANCELLED: { color: 'warning', text: 'Otkazano', icon: <ExclamationCircleOutlined /> },
}

const platformIcons: Record<SocialPlatform, React.ReactNode> = {
  INSTAGRAM: <InstagramOutlined style={{ color: '#E4405F', fontSize: 20 }} />,
  FACEBOOK: <FacebookOutlined style={{ color: '#1877F2', fontSize: 20 }} />,
  TIKTOK: <span style={{ fontSize: 16, fontWeight: 'bold' }}>TT</span>,
  TWITTER: <TwitterOutlined style={{ color: '#1DA1F2', fontSize: 20 }} />,
}

const platformLabels: Record<SocialPlatform, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TIKTOK: 'TikTok',
  TWITTER: 'X (Twitter)',
}

export default function SocialMediaShowPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const invalidate = useInvalidate()

  const { query } = useShow<SocialPost>({
    resource: 'social-media',
    id,
  })

  const post = query?.data?.data
  const isLoading = query?.isLoading

  // Publish now
  const handlePublishNow = async () => {
    if (!id) return
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/social-media/posts/${id}/publish`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Objavljivanje nije uspelo')
      }

      message.success('Post je poslat na objavljivanje')
      invalidate({ resource: 'social-media', invalidates: ['detail', 'list'] })
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo'}`)
    }
  }

  // Cancel scheduled post
  const handleCancel = async () => {
    if (!id) return
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/social-media/posts/${id}/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Otkazivanje nije uspelo')
      }

      message.success('Zakazani post je otkazan')
      invalidate({ resource: 'social-media', invalidates: ['detail', 'list'] })
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo'}`)
    }
  }

  // Delete post
  const handleDelete = async () => {
    if (!id) return
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/social-media/posts/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Brisanje nije uspelo')
      }

      message.success('Post je obrisan')
      navigate('/social-media')
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo'}`)
    }
  }

  if (!post && !isLoading) {
    return <Alert message="Post nije pronadjen" type="error" />
  }

  return (
    <Show
      title="Detalji posta"
      isLoading={isLoading}
      headerButtons={
        <Space>
          {post?.status === 'DRAFT' && (
            <>
              <Button icon={<EditOutlined />} onClick={() => navigate(`/social-media/${id}/edit`)}>
                Izmeni
              </Button>
              <Button type="primary" icon={<SendOutlined />} onClick={handlePublishNow}>
                Objavi odmah
              </Button>
            </>
          )}
          {post?.status === 'SCHEDULED' && (
            <Button icon={<StopOutlined />} onClick={handleCancel}>
              Otkazi
            </Button>
          )}
          {post?.status !== 'PUBLISHED' && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Obrisi
            </Button>
          )}
        </Space>
      }
    >
      {post && (
        <Row gutter={24}>
          {/* Main content */}
          <Col xs={24} lg={16}>
            <Card title="Sadrzaj" style={{ marginBottom: 16 }}>
              <Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: 16 }}>
                {post.content}
              </Paragraph>
              <Text type="secondary">{post.content.length} karaktera</Text>
            </Card>

            {/* Media */}
            {post.mediaUrls.length > 0 && (
              <Card title="Mediji" style={{ marginBottom: 16 }}>
                <Space wrap>
                  {post.mediaUrls.map((url, index) => (
                    <Image
                      key={index}
                      src={url}
                      width={120}
                      height={120}
                      style={{ objectFit: 'cover', borderRadius: 8 }}
                    />
                  ))}
                </Space>
              </Card>
            )}

            {/* Publish Results */}
            {post.publishResults && Object.keys(post.publishResults).length > 0 && (
              <Card title="Rezultati objavljivanja">
                {Object.entries(post.publishResults).map(([platform, result]) => (
                  <div key={platform} style={{ marginBottom: 16 }}>
                    <Space>
                      {platformIcons[platform as SocialPlatform]}
                      <Text strong>{platformLabels[platform as SocialPlatform]}</Text>
                      {result.success ? (
                        <Tag color="success">Uspesno</Tag>
                      ) : (
                        <Tag color="error">Neuspesno</Tag>
                      )}
                    </Space>
                    {result.platformUrl && (
                      <div style={{ marginTop: 8 }}>
                        <a href={result.platformUrl} target="_blank" rel="noopener noreferrer">
                          Pogledaj na platformi
                        </a>
                      </div>
                    )}
                    {result.error && (
                      <Alert message={result.error} type="error" style={{ marginTop: 8 }} />
                    )}
                  </div>
                ))}
              </Card>
            )}

            {/* Error message */}
            {post.errorMessage && (
              <Alert
                message="Greska pri objavljivanju"
                description={post.errorMessage}
                type="error"
                style={{ marginBottom: 16 }}
              />
            )}
          </Col>

          {/* Sidebar */}
          <Col xs={24} lg={8}>
            <Card style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Status">
                  <Tag color={statusConfig[post.status].color} icon={statusConfig[post.status].icon}>
                    {statusConfig[post.status].text}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Platforme">
                  <Space>
                    {post.platforms.map((platform) => (
                      <Tag key={platform}>
                        {platformIcons[platform]} {platformLabels[platform]}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <Descriptions column={1} size="small">
                {post.scheduledAt && (
                  <Descriptions.Item label="Zakazano za">
                    {new Date(post.scheduledAt).toLocaleString('sr-Latn-RS')}
                  </Descriptions.Item>
                )}
                {post.publishedAt && (
                  <Descriptions.Item label="Objavljeno">
                    {new Date(post.publishedAt).toLocaleString('sr-Latn-RS')}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Kreirano">
                  {new Date(post.createdAt).toLocaleString('sr-Latn-RS')}
                </Descriptions.Item>
                <Descriptions.Item label="Azurirano">
                  {new Date(post.updatedAt).toLocaleString('sr-Latn-RS')}
                </Descriptions.Item>
                <Descriptions.Item label="Vremenska zona">{post.timezone}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      )}
    </Show>
  )
}
